<template>
  <div>
    <h1 class="text-center my-2">Assemblies</h1>
    <add-or-update-assembly-form
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData" @resetUpdateData="resetUpdateData"
    />

    <list-table :records="records" :columns="tableColumns" :total-records="totalRecords" :search-filter.sync="search"
      :current-page-number.sync="currentPage" :show-search="true" :show-filter-by-default="true"
>
      <template v-if="can('assemblies:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add assembly</span>
        </b-button>
      </template>

      <template #cell(mission_zone)="data">
        <b-badge pill :variant="`light-${resolveStatusVariant(data.item.mission_zone ? 'yes' : 'no')}`"
          class="text-capitalize"
>
          {{ data.item.mission_zone ? "YES" : "NO" }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
          </template>

          <b-dropdown-item v-if="can('assemblies:CanUpdate')" @click="toggleMissionZone(data.item)">
            <feather-icon icon="MapPinIcon" />
            <span class="align-middle ml-50">
              {{ !data.item.mission_zone ? 'Mark as Mission Zone' : 'Unmark as Mission Zone' }}
            </span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('assemblies:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">
              Edit
            </span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('assemblies:CanDelete')" @click="onDelete(data.item._id)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">
              Delete
            </span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateAssemblyForm from '@/@core/components/shared/geo/AddOrUpdateAssemblyForm.vue';

import { debounce, get } from "lodash";
import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler() {
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler() {
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    BDropdownItem,
    AddOrUpdateAssemblyForm,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      records: [],

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'title', label: "Assembly", sortable: false },
        { key: 'assembly_type', sortable: false },
        { key: 'mission_zone', sortable: false },
        { key: 'district.title', label: "District", sortable: false },
        { key: 'district.area.title', label: "Area", sortable: false },
        { key: 'district.area.region.title', label: "Region", sortable: false },
        // { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get() {
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search,
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
        }
        await this.$router.push({ query }).catch(() => { });

        const request = await this.useJwt().sharedService.fetchGeoAssemblies(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(id){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this record?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.deleteGeoAssemblies(id)

        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Record deleted successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$nextTick(() => {
          this.toast({
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'error',
            text: error_message
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async toggleMissionZone(assembly) {
      try {
        this.loading = true;

        const mark = assembly.mission_zone ? "mark" : "unmark";

        const result = await this.$swal({
          title: 'Confirm Mission Zone Update?',
          text: `Do you want to ${mark} this assembly as a mission zone?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.updateGeoAssemblies(assembly.id, { mission_zone: !assembly.mission_zone });
        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'District updated successfully.'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onEdit(update) {
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData() {
      if (this.updateData) {
        this.updateData = null
      }
    },
  }
}
</script>
